import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/Button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dropzone, DropzoneContent, DropzoneEmptyState } from './ui/shadcn-io/dropzone';
import { AnimatedCircularProgressBar } from './magicui/animated-circular-progress-bar';
import { RainbowButton } from './magicui/rainbow-button';
import { ScrollArea } from './ui/scroll-area';
import { useFileUpload } from '../hooks/useFileUpload';
import { useProcessingStatus } from '../hooks/useProcessingStatus';
import { CheckCircle, FileText, AlertTriangle, RefreshCw, Upload as UploadIcon, X, Info, Plus, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../lib/utils';
import CustomTooltip from './ui/CustomTooltip';
import TalRevAPI from '../services/api';
import { TalentCardData } from '../types';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ModalView = 'form' | 'uploading' | 'processing';

interface ProcessingCard {
  id: string;
  employeeName: string;
  progress: number;
  currentStage: string;
}

const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { uploadState, uploadFile, resetUpload } = useFileUpload();

  // Form state
  const [roleType, setRoleType] = useState<'individual_contributor' | 'manager'>('individual_contributor');
  const [alias, setAlias] = useState('');
  const [aliasError, setAliasError] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Processing limit state
  const [activeCount, setActiveCount] = useState(0);
  const [processingCards, setProcessingCards] = useState<ProcessingCard[]>([]);
  const [isCheckingLimit, setIsCheckingLimit] = useState(false);
  const [isProcessingLimitExpanded, setIsProcessingLimitExpanded] = useState(false);

  // Modal view state
  const [view, setView] = useState<ModalView>('form');

  // Processing status
  const { status: processing, error: processingError } = useProcessingStatus(sessionId || undefined);
  const [isCompleted, setIsCompleted] = useState(false);

  // Fetch processing status and active count
  const fetchProcessingStatus = async () => {
    try {
      setIsCheckingLimit(true);
      const data = await TalRevAPI.listTalentCards();
      setActiveCount(data.activeCount);

      // Filter and extract processing cards (any card not completed or failed)
      const processing = data.talentCards
        .filter((tc: TalentCardData) => tc.status !== 'completed' && tc.status !== 'failed')
        .map((tc: TalentCardData) => ({
          id: tc.id,
          employeeName: tc.employeeProfile.name,
          progress: tc.metrics?.progress || 0,
          currentStage: tc.metrics?.currentStage || 'Processing...',
        }))
        .sort((a, b) => a.id.localeCompare(b.id)); // Sort by ID for stable ordering

      setProcessingCards(processing);
    } catch (error) {
      console.error('Failed to fetch processing status:', error);
    } finally {
      setIsCheckingLimit(false);
    }
  };

  // Fetch processing status when modal opens and set up polling
  useEffect(() => {
    if (isOpen && view === 'form') {
      fetchProcessingStatus();

      // Poll every 10 seconds when modal is open
      const interval = setInterval(() => {
        fetchProcessingStatus();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [isOpen, view]);

  // Handle upload state changes
  useEffect(() => {
    if (uploadState.status === 'uploading') {
      setView('uploading');
    } else if (uploadState.status === 'success' && sessionId) {
      setView('processing');
    } else if (uploadState.status === 'error') {
      setView('form');
    }
  }, [uploadState.status, sessionId]);

  // Handle processing completion
  useEffect(() => {
    if (!processing || view !== 'processing') return;

    if (processing.status === 'completed') {
      setIsCompleted(true);
    }
  }, [processing, processing?.status, view]);

  // Reset modal state when closed
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setView('form');
        setRoleType('individual_contributor');
        setAlias('');
        setAliasError('');
        setSelectedFile(null);
        setSessionId(null);
        setIsCompleted(false);
        resetUpload();
      }, 300); // Delay to allow modal close animation
    }
  }, [isOpen, resetUpload]);

  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  const validateAlias = (value: string): string => {
    // If alias is empty, it's valid (since it's optional)
    if (!value.trim()) {
      return '';
    }

    // Check if starts with '@'
    if (!value.startsWith('@')) {
      return 'Alias must start with @';
    }

    // Check for spaces
    if (value.includes(' ')) {
      return 'Alias cannot contain spaces';
    }

    // Check if all lowercase
    if (value !== value.toLowerCase()) {
      return 'Alias must be all lowercase';
    }

    return '';
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      const talentCardId = await uploadFile(selectedFile, roleType, alias || undefined);

      if (talentCardId) {
        setSessionId(talentCardId);
      }
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  const handleNavigateToDashboard = () => {
    onClose();
    navigate('/');
  };

  const handleNavigateToTalents = () => {
    onClose();
    navigate('/talents');
  };

  const handleViewTalentCard = () => {
    onClose();
    navigate(`/talents/${sessionId}`);
  };

  const handleTryAgain = () => {
    setView('form');
    setSelectedFile(null);
    setSessionId(null);
    setIsCompleted(false);
    resetUpload();
  };

  const getProgressText = () => {
    if (isCompleted) return 'Analysis complete!';

    // Check for processing errors from API (processing.error field)
    const apiError = (processing as any)?.error;
    const errorToCheck = processingError || apiError;

    if (errorToCheck) {
      // Check for AI extraction error
      if (errorToCheck.includes('AI was unable to extract') ||
          errorToCheck.includes('extract employee information')) {
        return 'Our AI had trouble reading the talent card. This is usually temporary - please try uploading again.';
      }

      // Check for session expired
      if (errorToCheck.includes('session may have expired') ||
          errorToCheck.includes('server was restarted')) {
        return 'Please upload your file again to restart processing';
      }

      // Generic error
      return 'Please try again or contact support';
    }

    // Check for failed status
    if (processing?.status === 'error' || processing?.status === 'failed') {
      return 'Processing failed. Please try uploading again.';
    }

    // Normal processing state
    if (processing?.status === 'processing') {
      return processing.currentStage || 'Processing your file...';
    }

    return 'This may take a few minutes';
  };

  const getDisplayFileName = () => {
    return processing?.filename || selectedFile?.name || 'your file';
  };

  const renderFormView = () => (
    <>
      <DialogHeader>
        <DialogTitle className="text-center text-[var(--font-size-xl)] sm:text-[var(--font-size-2xl)] font-[var(--font-weight-bold)]">
          Upload talent card
        </DialogTitle>
        <DialogDescription className="sr-only">
          Upload a PowerPoint talent card file for AI-powered analysis and calibration
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-2.5 sm:space-y-6 py-0 sm:py-4">
        {/* Processing Limit Status */}
        {activeCount >= 2 ? (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 sm:p-3 space-y-1.5 sm:space-y-2">
            <button
              onClick={() => setIsProcessingLimitExpanded(!isProcessingLimitExpanded)}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <h3 className="text-[var(--font-size-sm)] font-[var(--font-weight-semibold)] text-orange-900">
                  Processing Limit Reached (2/2)
                </h3>
              </div>
              {isProcessingLimitExpanded ? (
                <ChevronUp className="w-4 h-4 text-orange-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-orange-600" />
              )}
            </button>

            {isProcessingLimitExpanded && (
              <>
                <div className="space-y-1.5 sm:space-y-2">
                  {processingCards.slice(0, 2).map((card, index) => (
                    <div key={card.id} className="bg-white rounded-md p-1.5 sm:p-2 space-y-0.5 sm:space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[var(--font-size-xs)] font-[var(--font-weight-medium)] text-gray-900">
                          {index + 1}. {card.employeeName}
                        </span>
                        <span className="text-[var(--font-size-xs)] text-gray-600">
                          {card.progress}%
                        </span>
                      </div>
                      <div className="text-[var(--font-size-xs)] text-gray-600">
                        {card.currentStage}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-orange-500 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${card.progress}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <p className="text-[var(--font-size-xs)] text-orange-800">
                  Please wait for one to complete. We have limited quota for Bedrock usage.
                </p>
              </>
            )}
          </div>
        ) : activeCount === 1 ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 space-y-1.5 sm:space-y-2">
            <button
              onClick={() => setIsProcessingLimitExpanded(!isProcessingLimitExpanded)}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <h3 className="text-[var(--font-size-sm)] font-[var(--font-weight-semibold)] text-blue-900">
                  Processing Available (1/2 slots used)
                </h3>
              </div>
              {isProcessingLimitExpanded ? (
                <ChevronUp className="w-4 h-4 text-blue-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-blue-600" />
              )}
            </button>

            {isProcessingLimitExpanded && (
              <>
                {processingCards.slice(0, 1).map((card) => (
                  <div key={card.id} className="bg-white rounded-md p-1.5 sm:p-2 space-y-0.5 sm:space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[var(--font-size-xs)] font-[var(--font-weight-medium)] text-gray-900">
                        {card.employeeName}
                      </span>
                      <span className="text-[var(--font-size-xs)] text-gray-600">
                        {card.progress}%
                      </span>
                    </div>
                    <div className="text-[var(--font-size-xs)] text-gray-600">
                      {card.currentStage}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${card.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-green-900">
                Ready to Process (0/2 slots used)
              </span>
            </div>
          </div>
        )}

        {/* Role Type */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="roleType" className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)]">
              Role type
            </Label>
            <CustomTooltip
              content={
                <div className="max-w-sm">
                  AI Agents will review talents based on Amazon's General Role Guidelines (GLG) to calibrate performance and ensure consistency with Amazon's high bar for hiring and promoting employees.
                </div>
              }
              placement="top"
              showArrow={true}
            >
              <Info className="h-4 w-4 text-gray-400 cursor-help" />
            </CustomTooltip>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <button
              type="button"
              onClick={() => setRoleType('individual_contributor')}
              className={cn(
                "flex-1 px-4 py-2 rounded-md border-2 transition-colors text-[var(--font-size-sm)] font-[var(--font-weight-medium)]",
                roleType === 'individual_contributor'
                  ? "border-blue-600 bg-blue-50 text-blue-900"
                  : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
              )}
            >
              Individual Contributor
            </button>
            <button
              type="button"
              onClick={() => setRoleType('manager')}
              className={cn(
                "flex-1 px-4 py-2 rounded-md border-2 transition-colors text-[var(--font-size-sm)] font-[var(--font-weight-medium)]",
                roleType === 'manager'
                  ? "border-blue-600 bg-blue-50 text-blue-900"
                  : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
              )}
            >
              Manager
            </button>
          </div>
        </div>

        {/* Alias */}
        <div className="space-y-2">
          <Label htmlFor="alias" className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)]">
            Alias <span className="text-gray-500 font-normal">(optional)</span>
          </Label>
          <Input
            id="alias"
            type="text"
            placeholder="@alias"
            value={alias}
            onChange={(e) => {
              const value = e.target.value;
              setAlias(value);
              setAliasError(validateAlias(value));
            }}
            className="text-[var(--font-size-sm)]"
          />
          {aliasError && (
            <p className="text-[var(--font-size-xs)] text-red-600 mt-1">
              {aliasError}
            </p>
          )}
        </div>

        {/* File Upload Dropzone */}
        <div className="space-y-2">
          <Dropzone
            accept={{
              'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
            }}
            maxFiles={1}
            maxSize={25 * 1024 * 1024} // 25MB
            disabled={uploadState.status === 'uploading'}
            onDrop={handleFileSelect}
            onError={(error) => console.error('Dropzone error:', error)}
            className="min-h-[120px] sm:min-h-[180px] border-2 border-dashed rounded-lg"
            src={selectedFile ? [selectedFile] : undefined}
          >
            <DropzoneEmptyState>
              <div className="flex flex-col items-center justify-center text-center space-y-1.5 sm:space-y-3 py-3 sm:py-8">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#232F3E] rounded-full flex items-center justify-center">
                  <UploadIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <p className="text-[var(--font-size-sm)] sm:text-[var(--font-size-base)] font-[var(--font-weight-medium)] text-gray-900">
                    Drag and Drop file here or{' '}
                    <span className="text-blue-600 underline cursor-pointer">Choose file</span>
                  </p>
                </div>
              </div>
            </DropzoneEmptyState>
            <DropzoneContent>
              {selectedFile && (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-lg">
                    <FileText className="w-5 h-5 text-gray-600" />
                    <span className="text-[var(--font-size-sm)] text-gray-700">
                      {selectedFile.name}
                    </span>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setSelectedFile(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          e.preventDefault();
                          setSelectedFile(null);
                        }
                      }}
                      className="text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <X className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              )}
            </DropzoneContent>
          </Dropzone>
          <div className="text-[var(--font-size-xs)] text-gray-500">
            <span>Supported formats: PPTX</span>
          </div>
        </div>

        {/* Example Downloads */}
        <div className="bg-gray-50 rounded-lg p-2 sm:p-4 space-y-2 sm:space-y-3">
          {/* Talent Card Example */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0">
                <svg viewBox="0 0 48 48" className="w-full h-full">
                  {/* PowerPoint icon */}
                  <rect x="6" y="4" width="36" height="40" fill="#D04423" rx="2"/>
                  <path d="M10,10 h28 v4 h-28 z M10,16 h28 v2 h-28 z M10,20 h28 v2 h-28 z M10,24 h18 v2 h-18 z M10,28 h18 v2 h-18 z" fill="#ffffff" opacity="0.4"/>
                  <text x="24" y="38" fontFamily="Arial, sans-serif" fontSize="16" fill="#ffffff" fontWeight="bold" textAnchor="middle">P</text>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-[var(--font-size-xs)] sm:text-[var(--font-size-sm)] font-[var(--font-weight-semibold)] text-gray-900 mb-0.5 sm:mb-1">
                  Talent card example
                </h4>
                <p className="text-[var(--font-size-xs)] text-gray-600">
                  You can download the attached example and use them as a starting point for your own file.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const link = document.createElement('a');
                link.href = '/talent-card-2.pptx';
                link.download = 'talent-card-example.pptx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              className="text-[var(--font-size-xs)] flex-shrink-0 w-full sm:w-auto"
            >
              Download
            </Button>
          </div>

          {/* GLG Examples */}
          <div className="border-t pt-2 sm:pt-3 space-y-1.5 sm:space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0">
                <svg viewBox="0 0 48 48" className="w-full h-full">
                  {/* Document icon */}
                  <rect x="8" y="4" width="32" height="40" fill="#4285F4" rx="2"/>
                  <path d="M12,12 h24 v2 h-24 z M12,18 h24 v2 h-24 z M12,24 h18 v2 h-18 z M12,30 h20 v2 h-20 z" fill="#ffffff" opacity="0.7"/>
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-[var(--font-size-xs)] sm:text-[var(--font-size-sm)] font-[var(--font-weight-semibold)] text-gray-900">
                  Amazon General Level Guidelines (GLG)
                </h4>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pl-0 sm:pl-12">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = '/General_Level_Guideline_Tech_IC (1).docx';
                  link.download = 'Amazon-GLG-Tech-IC.docx';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="text-[var(--font-size-xs)] flex-1"
              >
                GLG for IC
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = '/General_Level_Guideline_People_Manager (2).docx';
                  link.download = 'Amazon-GLG-People-Manager.docx';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="text-[var(--font-size-xs)] flex-1"
              >
                GLG for Managers
              </Button>
            </div>
          </div>
        </div>

        {/* Upload Error */}
        {uploadState.status === 'error' && uploadState.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-[var(--font-size-sm)] text-red-800">
              {uploadState.error}
            </p>
          </div>
        )}
      </div>

      <DialogFooter className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 border-t pt-2 sm:pt-4 mt-2 sm:mt-0">
        <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
          Cancel
        </Button>
        <RainbowButton
          onClick={handleUpload}
          disabled={!selectedFile || uploadState.status === 'uploading' || activeCount >= 2 || !!aliasError}
          className="flex items-center justify-center space-x-2 h-10 w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 text-white" />
          <span className="text-white text-[var(--font-size-xs)] sm:text-[var(--font-size-sm)]">
            {activeCount >= 2 ? 'Processing Limit Reached' : 'Upload'}
          </span>
        </RainbowButton>
      </DialogFooter>
    </>
  );

  const renderUploadingView = () => (
    <>
      <DialogHeader>
        <DialogTitle className="text-center flex flex-col sm:flex-row items-center justify-center gap-2 text-[var(--font-size-base)] sm:text-[var(--font-size-lg)]">
          <FileText className="w-5 h-5 text-[var(--info-color)]" />
          <span className="truncate max-w-full">Uploading {selectedFile?.name}</span>
        </DialogTitle>
        <DialogDescription className="sr-only">
          File upload in progress
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col items-center justify-center space-y-4 sm:space-y-6 py-8 sm:py-12 min-h-[200px] sm:min-h-[250px]">
        <div className="w-full max-w-xs space-y-3 sm:space-y-4 px-4 sm:px-0">
          <div className="text-center">
            <p className="text-[var(--font-size-sm)] text-gray-600 mb-2">
              {selectedFile && `${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`}
            </p>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadState.progress}%` }}
            />
          </div>
          <p className="text-[var(--font-size-sm)] text-gray-600 text-center">
            {uploadState.progress}% complete
          </p>
        </div>
      </div>

      <DialogFooter className="border-t pt-4">
        <div className="flex gap-2 justify-center w-full">
          <Button variant="outline" size="sm" onClick={onClose} className="w-full sm:w-auto">
            Close
          </Button>
        </div>
      </DialogFooter>
    </>
  );

  const renderProcessingView = () => {
    // Check for errors from both hook and API
    const apiError = (processing as any)?.error;
    const hasError = processingError || apiError || processing?.status === 'failed' || processing?.status === 'error';
    const errorToCheck = processingError || apiError;
    const isSessionExpired = errorToCheck && (errorToCheck.includes('session may have expired') || errorToCheck.includes('server was restarted'));

    return (
    <>
      <DialogHeader>
        <DialogTitle className="text-center flex flex-col sm:flex-row items-center justify-center gap-2 text-[var(--font-size-base)] sm:text-[var(--font-size-lg)]">
          {isCompleted ? (
            <CheckCircle className="w-5 h-5 text-[var(--success-color)]" />
          ) : isSessionExpired ? (
            <AlertTriangle className="w-5 h-5 text-orange-500" />
          ) : hasError ? (
            <AlertTriangle className="w-5 h-5 text-[var(--error-color)]" />
          ) : (
            <FileText className="w-5 h-5 text-[var(--info-color)]" />
          )}
          <span className="truncate max-w-full">
            {isCompleted ? 'Processing Complete!' :
             isSessionExpired ? 'Session Expired' :
             hasError ? 'Processing Error' :
             `Processing ${getDisplayFileName()}`}
          </span>
        </DialogTitle>
        <DialogDescription className="sr-only">
          {isCompleted ? 'AI analysis completed successfully' :
           isSessionExpired ? 'Processing session has expired' :
           hasError ? 'An error occurred during processing' :
           'AI analysis in progress'}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col items-center justify-center space-y-4 sm:space-y-6 py-8 sm:py-12 min-h-[200px] sm:min-h-[250px] px-4 sm:px-0">
        <AnimatedCircularProgressBar
          value={processing?.progress || 0}
          gaugePrimaryColor={isCompleted ? "rgb(34 197 94)" : "rgb(79 70 229)"}
          gaugeSecondaryColor="rgba(0, 0, 0, 0.1)"
        />

        <div className="text-center space-y-2">
          <p className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)]">
            {isCompleted ? 'Ready to view!' : 'Processing in progress...'}
          </p>
          <p className="text-[var(--font-size-xs)] text-[var(--text-tertiary)]">
            {getProgressText()}
          </p>
        </div>
      </div>

      <div className="border-t pt-4">
        {isCompleted ? (
          <div className="space-y-3 sm:space-y-4">
            <p className="text-[var(--font-size-sm)] text-center text-[var(--success-color)] font-[var(--font-weight-medium)]">
              Your talent card is ready for review!
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                onClick={handleViewTalentCard}
                className="bg-green-600 hover:bg-green-700"
              >
                View Talent Card
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNavigateToDashboard}
              >
                Dashboard
              </Button>
            </div>
          </div>
        ) : isSessionExpired ? (
          <div className="space-y-3 sm:space-y-4">
            <p className="text-[var(--font-size-sm)] text-center text-orange-600 font-[var(--font-weight-medium)]">
              Your processing session has expired. This can happen when the server restarts.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                onClick={handleTryAgain}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Upload Again
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNavigateToDashboard}
              >
                Dashboard
              </Button>
            </div>
          </div>
        ) : hasError ? (
          <div className="space-y-3 sm:space-y-4">
            <p className="text-[var(--font-size-sm)] text-center text-[var(--error-color)] font-[var(--font-weight-medium)]">
              {getProgressText()}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                onClick={handleTryAgain}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNavigateToDashboard}
              >
                Dashboard
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-[var(--font-size-xs)] text-[var(--text-tertiary)] text-center mb-3 sm:mb-4 px-4 sm:px-0">
              Processing is happening in the background. You can safely close this modal.
            </p>

            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={handleNavigateToDashboard}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNavigateToTalents}
              >
                View Talents
              </Button>
            </div>
          </>
        )}
      </div>
    </>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:w-[95vw] max-w-[540px] h-auto max-h-[85vh] min-h-0 p-0">
        <ScrollArea className="max-h-[85vh]">
          <div className="p-3 sm:p-6">
            {view === 'form' && renderFormView()}
            {view === 'uploading' && renderUploadingView()}
            {view === 'processing' && renderProcessingView()}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default UploadModal;
